import { Component, OnInit, OnDestroy, HostListener, HostBinding } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Chart, registerables, Plugin, ScriptableContext } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { CARGO_HEATING_SEGMENTS, mapCargoHeatingSegmentsToApi } from '../cargo-heating-segments';

Chart.register(...registerables, annotationPlugin);
interface VoyageTableRow {
  gradientRange: string;
  [key: string]: any;
}
@Component({
  selector: 'app-pivot-table',
  templateUrl: './pivot-table.component.html',
  styleUrls: ['./pivot-table.component.scss'],
})
export class PivotTableComponent implements OnInit, OnDestroy {
  myForm!: FormGroup;
  username: any;
  // 'All' is a select-all shortcut; the actual segment values come from the
  // single source of truth (New Voyage form) so every filter is consistent.
  segmentOptions: string[] = ['All', ...CARGO_HEATING_SEGMENTS];
  voyageTableColumns: string[] = [];
  voyageTableRows: VoyageTableRow[] = [];
  filteredVoyageRows: VoyageTableRow[] = [];
  recordedTime = '';

  cargoOptions: string[] = ['Fuel Oil', 'Crude Oil', 'Veg Oil', 'VGO'];
  heatFlagOptions: string[] = ['Yes', 'No'];
  tankOptions: string[] = [
    'All',
    '1P',
    '1S',
    '1C',
    '2P',
    '2S',
    '2C',
    '3P',
    '3S',
    '3C',
    '4P',
    '4S',
    '4C',
    '5P',
    '5S',
    '5C',
    '6P',
    '6S',
    '6C',
    '7P',
    '7S',
    '7C',
    '8P',
    '8S',
    '8C',
    '9P',
    '9S',
    '9C',
    'SLP',
    'SLS',
    'SLC',
  ];

  selectedSegments: string[] = [];
  selectedHeatFlags: string[] = [];
  selectedCargoTypes: string[] = [];
  selectedTanks: string[] = [];
  isAdminView = false; // default Client View
  isFilterSubmitted = false;
  /** Drives the skeleton loader while pivot data is loading. */
  isLoading = true;
  showShipTable = false;
  noDataFound = false;
  segmentGroups: any[] = [];
  segmentDropdownOpen = false;
  tankDropdownOpen = false;

  filteredSegmentList: string[] = [];
  filteredTankList: string[] = [];

  selectedSegmentLabel = '';
  selectedTankLabel = '';

  // Modern Filter State
  segmentSearchQuery: string = '';
  tankSearchQuery: string = '';
  cargoSearchQuery: string = '';
  heatFlagSearchQuery: string = '';
  cargoDropdownOpen = false;
  heatFlagDropdownOpen = false;

  public temperatureDrops: number[] = [];
  totalShipData: Ship[] = [];


  summaryStats = [
    { label: 'Total Records', value: 0 },
    { label: 'Average Temperature Drop', value: '0 °C' },
    { label: 'Median Temperature Drop', value: '0 °C' },
    { label: 'Minimum Temperature Drop', value: '0 °C' },
    { label: 'Maximum Temperature Drop', value: '0 °C' },
    { label: 'Total Cargo Grades', value: 0 },
    { label: 'Total Ships', value: 0 },
    { label: 'Total Voyages', value: 0 },
  ];

  private standardDeviationChart?: Chart<'line'>;
  private doughnutChart?: Chart<'doughnut'>;
  @ViewChild('voyageDrawer') voyageDrawer!: MatDrawer;

  filteredShipData: Ship[] = [];

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private commonService: CargoheatingCommonService,
    private router: Router,
    private requestFormService: RequestFormService

  ) { }

  ngOnInit(): void {
    this.username = localStorage.getItem('username');
    this.myForm = this.fb.group({
      enableGradient: [false],
      groupMin: ['-1000', Validators.required],
      groupMax: ['1000', Validators.required],
    });

    this.selectedSegments = [...this.segmentOptions];
    this.selectedHeatFlags = [...this.heatFlagOptions];
    this.selectedTanks = this.tankOptions.filter((t) => t !== 'All');
    this.selectedCargoTypes = [...this.cargoOptions];
    this.filteredSegmentList = [...this.segmentOptions];
    this.filteredTankList = [...this.tankOptions];
    this.fetchData();
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  get enableGradientControl() {
    return this.myForm.get('enableGradient') as FormControl;
  }
  filterSegments(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value.toLowerCase();

    this.filteredSegmentList = this.segmentOptions.filter(seg =>
      seg.toLowerCase().includes(value)
    );
  }

  // onSegmentCheckboxChange(event: any, segment: string) {
  //   if (event.target.checked) {
  //     this.selectedSegments.push(segment);
  //   } else {
  //     this.selectedSegments =
  //       this.selectedSegments.filter(s => s !== segment);
  //   }

  //   this.selectedSegmentLabel = this.selectedSegments.join(', ');
  // }
  onSegmentCheckboxChange(event: any, segment: string) {

    const checked = event.target.checked;

    if (segment === 'All') {

      if (checked) {
        // Select all
        this.selectedSegments = [...this.segmentOptions];
      } else {
        // Unselect all
        this.selectedSegments = [];
      }

    } else {

      if (checked) {
        this.selectedSegments.push(segment);
      } else {
        this.selectedSegments =
          this.selectedSegments.filter(s => s !== segment);
      }

      // Remove duplicate
      this.selectedSegments = [...new Set(this.selectedSegments)];

      // Check if all individual selected
      const allWithoutAll =
        this.segmentOptions.filter(s => s !== 'All');

      const allSelected =
        allWithoutAll.every(s => this.selectedSegments.includes(s));

      if (allSelected) {
        if (!this.selectedSegments.includes('All')) {
          this.selectedSegments.push('All');
        }
      } else {
        this.selectedSegments =
          this.selectedSegments.filter(s => s !== 'All');
      }
    }

    this.selectedSegmentLabel =
      this.selectedSegments.join(', ');
  }
  closeSegmentDropdown() {
    setTimeout(() => {
      this.segmentDropdownOpen = false;
    }, 200);
  }
  filterTanks(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value.toLowerCase();

    this.filteredTankList = this.tankOptions.filter(t =>
      t.toLowerCase().includes(value)
    );
  }

  // onTankCheckboxChange(event: any, tank: string) {
  //   if (event.target.checked) {
  //     this.selectedTanks.push(tank);
  //   } else {
  //     this.selectedTanks =
  //       this.selectedTanks.filter(t => t !== tank);
  //   }

  //   this.selectedTankLabel = this.selectedTanks.join(', ');
  // }
  onTankCheckboxChange(event: any, tank: string) {

    const checked = event.target.checked;

    if (tank === 'All') {

      if (checked) {
        this.selectedTanks = [...this.tankOptions];
      } else {
        this.selectedTanks = [];
      }

    } else {

      if (checked) {
        this.selectedTanks.push(tank);
      } else {
        this.selectedTanks =
          this.selectedTanks.filter(t => t !== tank);
      }

      this.selectedTanks = [...new Set(this.selectedTanks)];

      const allWithoutAll =
        this.tankOptions.filter(t => t !== 'All');

      const allSelected =
        allWithoutAll.every(t => this.selectedTanks.includes(t));

      if (allSelected) {
        if (!this.selectedTanks.includes('All')) {
          this.selectedTanks.push('All');
        }
      } else {
        this.selectedTanks =
          this.selectedTanks.filter(t => t !== 'All');
      }
    }

    this.selectedTankLabel =
      this.selectedTanks.join(', ');
  }
  closeTankDropdown() {
    setTimeout(() => {
      this.tankDropdownOpen = false;
    }, 200);
  }

  // Modern Dropdown Toggles
  toggleCargoDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.cargoDropdownOpen = !this.cargoDropdownOpen;
    this.heatFlagDropdownOpen = false;
    this.segmentDropdownOpen = false;
    this.tankDropdownOpen = false;
  }

  toggleHeatFlagDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.heatFlagDropdownOpen = !this.heatFlagDropdownOpen;
    this.cargoDropdownOpen = false;
    this.segmentDropdownOpen = false;
    this.tankDropdownOpen = false;
  }

  toggleSegmentDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.segmentDropdownOpen = !this.segmentDropdownOpen;
    this.tankDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.heatFlagDropdownOpen = false;
  }

  toggleTankDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.tankDropdownOpen = !this.tankDropdownOpen;
    this.segmentDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.heatFlagDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.segmentDropdownOpen = false;
    this.tankDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.heatFlagDropdownOpen = false;
  }

  getFilteredSegments() {
    return this.segmentOptions.filter(opt => opt.toLowerCase().includes(this.segmentSearchQuery.toLowerCase()));
  }

  getFilteredTanks() {
    return this.tankOptions.filter(opt => opt.toLowerCase().includes(this.tankSearchQuery.toLowerCase()));
  }

  getFilteredCargos() {
    return this.cargoOptions.filter(opt => opt.toLowerCase().includes(this.cargoSearchQuery.toLowerCase()));
  }

  getFilteredHeatFlags() {
    return this.heatFlagOptions.filter(opt => opt.toLowerCase().includes(this.heatFlagSearchQuery.toLowerCase()));
  }

  // Segment Selection
  clearSegmentFilter(event?: Event) {
    if (event) event.stopPropagation();
    this.selectedSegments = [];
    this.selectedSegmentLabel = '';
  }

  toggleAllSegments(event: any) {
    const filtered = this.getFilteredSegments();
    if (event.target.checked) {
      const current = new Set(this.selectedSegments);
      filtered.forEach((s) => current.add(s));
      this.selectedSegments = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedSegments = this.selectedSegments.filter((s) => !filteredSet.has(s));
    }
    this.selectedSegmentLabel = this.selectedSegments.join(', ');
  }

  isAllSegmentsSelected() {
    const filtered = this.getFilteredSegments();
    return (
      filtered.length > 0 &&
      filtered.every((s) => this.selectedSegments.includes(s))
    );
  }

  // Tank Selection
  clearTankFilter(event?: Event) {
    if (event) event.stopPropagation();
    this.selectedTanks = [];
    this.selectedTankLabel = '';
  }

  toggleAllTanks(event: any) {
    const filtered = this.getFilteredTanks();
    if (event.target.checked) {
      const current = new Set(this.selectedTanks);
      filtered.forEach((t) => current.add(t));
      this.selectedTanks = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedTanks = this.selectedTanks.filter((t) => !filteredSet.has(t));
    }
    this.selectedTankLabel = this.selectedTanks.join(', ');
  }

  isAllTanksSelected() {
    const filtered = this.getFilteredTanks();
    return (
      filtered.length > 0 &&
      filtered.every((t) => this.selectedTanks.includes(t))
    );
  }

  // Cargo Selection
  clearCargoFilter(event?: Event) {
    if (event) event.stopPropagation();
    this.selectedCargoTypes = [];
  }

  toggleAllCargos(event: any) {
    const filtered = this.getFilteredCargos();
    if (event.target.checked) {
      const current = new Set(this.selectedCargoTypes);
      filtered.forEach((c) => current.add(c));
      this.selectedCargoTypes = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedCargoTypes = this.selectedCargoTypes.filter((c) => !filteredSet.has(c));
    }
  }

  isAllCargosSelected() {
    const filtered = this.getFilteredCargos();
    return (
      filtered.length > 0 &&
      filtered.every((c) => this.selectedCargoTypes.includes(c))
    );
  }

  onCargoCheckboxChange(event: any, cargo: string) {
    if (event.target.checked) {
      this.selectedCargoTypes.push(cargo);
    } else {
      this.selectedCargoTypes = this.selectedCargoTypes.filter(c => c !== cargo);
    }
  }

  // Heat Flag Selection
  clearHeatFlagFilter(event?: Event) {
    if (event) event.stopPropagation();
    this.selectedHeatFlags = [];
  }

  toggleAllHeatFlags(event: any) {
    const filtered = this.getFilteredHeatFlags();
    if (event.target.checked) {
      const current = new Set(this.selectedHeatFlags);
      filtered.forEach((f) => current.add(f));
      this.selectedHeatFlags = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedHeatFlags = this.selectedHeatFlags.filter((f) => !filteredSet.has(f));
    }
  }

  isAllHeatFlagsSelected() {
    const filtered = this.getFilteredHeatFlags();
    return (
      filtered.length > 0 &&
      filtered.every((f) => this.selectedHeatFlags.includes(f))
    );
  }

  onHeatFlagCheckboxChange(event: any, flag: string) {
    if (event.target.checked) {
      this.selectedHeatFlags.push(flag);
    } else {
      this.selectedHeatFlags = this.selectedHeatFlags.filter(f => f !== flag);
    }
  }

  clearFilters() {
    this.selectedSegments = [...this.segmentOptions];
    this.selectedHeatFlags = [...this.heatFlagOptions];
    this.selectedTanks = this.tankOptions.filter(t => t !== 'All');
    this.selectedCargoTypes = [...this.cargoOptions];
    this.selectedSegmentLabel = this.selectedSegments.join(', ');
    this.selectedTankLabel = this.selectedTanks.join(', ');
    this.segmentSearchQuery = '';
    this.tankSearchQuery = '';
    this.cargoSearchQuery = '';
    this.heatFlagSearchQuery = '';
    this.fetchData();
  }
  // toggleAdminView(event: any) {
  //   this.isAdminView = event.checked;

  //   if (this.isAdminView) {

  //     this.myForm.patchValue({
  //       groupMin: null,
  //       groupMax: null
  //     });
  //   } else {

  //     this.myForm.patchValue({
  //       groupMin: -1000,
  //       groupMax: 1000
  //     });
  //   }
  // }
  toggleAdminView(event: any) {
    this.isAdminView = event.checked;

    const groupMinControl = this.myForm.get('groupMin');
    const groupMaxControl = this.myForm.get('groupMax');

    if (this.isAdminView) {
      // Custom range ON → Required
      groupMinControl?.setValidators([Validators.required]);
      groupMaxControl?.setValidators([Validators.required]);

      groupMinControl?.setValue(null);
      groupMaxControl?.setValue(null);
    } else {
      // Custom range OFF → Remove validation
      groupMinControl?.clearValidators();
      groupMaxControl?.clearValidators();

      groupMinControl?.setValue(-1000);
      groupMaxControl?.setValue(1000);
    }

    groupMinControl?.updateValueAndValidity();
    groupMaxControl?.updateValueAndValidity();
  }



  toggleShipTable() {
    this.showShipTable = !this.showShipTable;
  }
  //onSegmentChange(selected: string[]) { this.selectedSegments = selected; }
  onHeatFlagChange(selected: string[]) {
    this.selectedHeatFlags = selected;
  }
  onCargoTypeChange(selected: string[]) {
    this.selectedCargoTypes = selected;
  }

  onSegmentChange(selected: string[]) {
    const allItems = [...this.segmentOptions];
    const allWithoutAll = allItems.filter((item) => item !== 'All');

    if (selected.includes('All')) {
      this.selectedSegments =
        this.selectedSegments.length === allItems.length ? [] : [...allItems];
    } else {
      this.selectedSegments = selected;
      const selectedWithoutAll = selected.filter((item) => item !== 'All');
      if (allWithoutAll.every((item) => selectedWithoutAll.includes(item))) {
        this.selectedSegments = [...allWithoutAll, 'All'];
      }
    }
  }

  onTankChange(selected: string[]) {
    const allItems = [...this.tankOptions];
    const allWithoutAll = allItems.filter((item) => item !== 'All');

    if (selected.includes('All')) {
      this.selectedTanks =
        this.selectedTanks.length === allItems.length ? [] : [...allItems];
    } else {
      this.selectedTanks = selected;
      const selectedWithoutAll = selected.filter((item) => item !== 'All');
      if (allWithoutAll.every((item) => selectedWithoutAll.includes(item))) {
        this.selectedTanks = [...allWithoutAll, 'All'];
      }
    }
  }

  // formatTempStat(label: string, value: number): { label: string; value: string } {
  //   const isRise = value < 0;
  //   const absValue = Math.abs(value).toFixed(2);
  //   const finalLabel = label.replace('Drop', isRise ? 'Rise' : 'Drop');
  //   return { label: finalLabel, value: `${absValue} °C` };
  // }
  formatTempStat(
    label: string,
    value: number | null | undefined
  ): { label: string; value: string } {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return { label, value: '—' }; // show dash if no data
    }

    const num = Number(value);
    const finalLabel = label.replace('Drop', num < 0 ? 'Rise' : 'Drop');
    return { label: finalLabel, value: `${num.toFixed(2)} °C` };
  }

  private buildPayload(): any {
    const form = this.myForm.value;

    return {
      segments: mapCargoHeatingSegmentsToApi(
        this.selectedSegments.filter((s) => s.toLowerCase() !== 'all'),
      ).map((s) => s.toLowerCase()),

      heat_flags:
        this.selectedHeatFlags.length > 0
          ? this.selectedHeatFlags.map(h => (h === 'Yes' ? 'Y' : 'N'))
          : ['Y', 'N'],

      grad_range_min: this.isAdminView
        ? +form.groupMin
        : -1000,

      grad_range_max: this.isAdminView
        ? +form.groupMax
        : 1000,

      cargo_types: this.selectedCargoTypes,

      tanks: this.selectedTanks
        .filter((t) => t !== 'All')
        .map((t) => t.toLowerCase()),
    };
  }

  fetchData(): void {


    if (this.isAdminView && this.myForm.invalid) {
      this.myForm.markAllAsTouched();
      this.toastr.error('Please fill in the required fields.');
      return;
    }

    this.isFilterSubmitted = true;
    this.isLoading = true;

    const form = this.myForm.value;

    const payload: any = {
      segments: mapCargoHeatingSegmentsToApi(
        this.selectedSegments.filter((s) => s.toLowerCase() !== 'all'),
      ).map((s) => s.toLowerCase()),

      heat_flags:
        this.selectedHeatFlags.length > 0
          ? this.selectedHeatFlags.map(h => (h === 'Yes' ? 'Y' : 'N'))
          : ['Y', 'N'],


      grad_range_min: this.isAdminView
        ? +form.groupMin
        : -1000,

      grad_range_max: this.isAdminView
        ? +form.groupMax
        : 1000,

      cargo_types: this.selectedCargoTypes,
    };

    const selectedTanks = this.selectedTanks
      .filter((t) => t.toLowerCase() !== 'all')
      .map((t) => t.toLowerCase());

    if (selectedTanks.length > 0) {
      payload.tanks = selectedTanks;
    }

    console.log('Payload sending to API:', payload);

    this.commonService.postPivotData(payload).subscribe({
      next: (res) => {
        if (res?.success && res.data) {
          this.toastr.success('Report fetched successfully.');
          this.temperatureDrops = (res.data.raw_temperature_drops || [])
            .map((val: any) => Number(val))
            .filter((v: number) => !isNaN(v));

          if (this.temperatureDrops.length === 0) {
            this.temperatureDrops = [
              res.data.min_tank_temp_drop,
              res.data.median_tank_temp_drop,
              res.data.max_tank_temp_drop,
            ].filter((v: number) => v !== null && v !== undefined);
          }

          const minValue =
            res.data.min_tank_temp_drop ?? Math.min(...this.temperatureDrops);

          const maxValue =
            res.data.max_tank_temp_drop ?? Math.max(...this.temperatureDrops);

          const median = res.data.median_tank_temp_drop ?? 0;

          const avgTempDrop =
            res.data.avg_tank_temp_drop ??
            this.calculateMean(this.temperatureDrops);

          const stdDev = this.calculateStandardDeviation(
            this.temperatureDrops,
            avgTempDrop
          );

          const { oneStdDevCount, twoStdDevCount, totalCount } =
            this.calculateStdDevDistribution(
              this.temperatureDrops,
              avgTempDrop,
              stdDev
            );

          this.summaryStats = [
            { label: 'Total Records', value: res.data.total_temp_gradient_count ?? 0 },
            this.formatTempStat('Average Temperature Drop', avgTempDrop),
            this.formatTempStat('Median Temperature Drop', res.data.median_tank_temp_drop),
            this.formatTempStat('Minimum Temperature Drop', res.data.min_tank_temp_drop),
            this.formatTempStat('Maximum Temperature Drop', res.data.max_tank_temp_drop),
            { label: 'Total Cargo Grades', value: res.data.distinct_oil_grade_count ?? 0 },
            { label: 'Total Ships', value: res.data.total_no_of_ships ?? 0 },
            { label: 'Total Voyages', value: res.data.total_no_of_voy ?? 0 },
            this.formatTempStat('Minimum Gradient', res.data.min_gradient),
            this.formatTempStat('Maximum Gradient', res.data.max_gradient),
          ];

          this.noDataFound = false;

          this.createCharts(
            avgTempDrop,
            minValue,
            maxValue,
            median,
            stdDev,
            oneStdDevCount,
            twoStdDevCount,
            totalCount
          );

        } else {
          this.resetSummaryStatsToZero();
          this.noDataFound = true;
          this.toastr.info('No data found for the selected filters.');
          this.destroyCharts();
        }
        this.isLoading = false;
      },
      error: () => {
        this.resetSummaryStatsToZero();
        this.noDataFound = true;
        this.toastr.error('An error occurred while fetching data.');
        this.destroyCharts();
        this.isLoading = false;
      },
    });
  }

  onStatClick(label: string) {

    if (this.username === 'Meraki - Operations' && label === 'Total Voyages') {
      const payload = this.buildPayload(); //  same payload reuse
      console.log('Payload sent on stat click:', payload); // debug check
      this.router.navigate(['/ch-totalShip'], { state: payload });
    }
  }

  private destroyCharts() {
    this.standardDeviationChart?.destroy();
    this.doughnutChart?.destroy();
    this.standardDeviationChart = undefined;
    this.doughnutChart = undefined;
  }

  private createCharts(
    mean: number,
    minValue: number,
    maxValue: number,
    median: number,
    stdDev: number,
    oneStdDevCount: number,
    twoStdDevCount: number,
    totalCount: number
  ) {
    this.destroyCharts();

    // Do not create charts if data is invalid or missing
    if (!totalCount || totalCount <= 0 || isNaN(mean) || isNaN(stdDev)) return;

    const oneStdDevPct = ((oneStdDevCount / totalCount) * 100).toFixed(2);
    const twoStdDevPct = ((twoStdDevCount / totalCount) * 100).toFixed(2);
    const outsideStdDevCount = totalCount - twoStdDevCount;
    const outsideStdDevPct = (100 - parseFloat(twoStdDevPct)).toFixed(2);

    const sdCtx = document.getElementById(
      'standardDeviationChart'
    ) as HTMLCanvasElement | null;
    if (sdCtx && stdDev > 0) {
      this.standardDeviationChart = new Chart(sdCtx, {
        type: 'line',
        data: {
          datasets: [
            {
              data: this.generateCurvePoints(mean, stdDev),
              fill: true,
              borderWidth: 3,
              borderColor: '#001f40',
              backgroundColor: (context: ScriptableContext<'line'>) => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;
                if (!chartArea) return 'rgba(0,0,0,0)';
                const gradient = ctx.createLinearGradient(
                  0,
                  chartArea.top,
                  0,
                  chartArea.bottom
                );
                gradient.addColorStop(0, '#6b7280');
                gradient.addColorStop(1, '#001f40');
                return gradient;
              },
              tension: 0.35,
              pointRadius: 0,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            annotation: {
              annotations: {
                // Main data points
                averageLine: {
                  type: 'line',
                  xMin: mean,
                  xMax: mean,
                  borderColor: '#001f40',
                  borderWidth: 2,
                  label: {
                    display: true,
                    content: [`Average (${mean.toFixed(2)})`],
                    position: 'start',
                  },
                },
                medianLine: {
                  type: 'line',
                  xMin: median,
                  xMax: median,
                  borderColor: '#ff9800',
                  borderWidth: 2,
                  label: {
                    display: true,
                    content: [`Median (${median.toFixed(2)})`],
                    position: 'start',
                  },
                },
                minLine: {
                  type: 'line',
                  xMin: minValue,
                  xMax: minValue,
                  borderColor: '#e53935',
                  borderWidth: 2,
                  label: {
                    display: true,
                    content: [`Min (${minValue.toFixed(2)})`],
                    position: 'start',
                  },
                },
                maxLine: {
                  type: 'line',
                  xMin: maxValue,
                  xMax: maxValue,
                  borderColor: '#43a047',
                  borderWidth: 2,
                  label: {
                    display: true,
                    content: [`Max (${maxValue.toFixed(2)})`],
                    position: 'start',
                  },
                },
                // 1st Standard Deviation lines
                oneStdDevPlus: {
                  type: 'line',
                  xMin: mean + stdDev,
                  xMax: mean + stdDev,
                  borderColor: 'rgba(255, 165, 0, 0.5)',
                  borderDash: [5, 5],
                  borderWidth: 2,
                  label: {
                    display: true,
                    content: [`+1σ (${(mean + stdDev).toFixed(2)})`],
                    position: 'end',
                  },
                },
                oneStdDevMinus: {
                  type: 'line',
                  xMin: mean - stdDev,
                  xMax: mean - stdDev,
                  borderColor: 'rgba(255, 165, 0, 0.5)',
                  borderDash: [5, 5],
                  borderWidth: 2,
                  label: {
                    display: true,
                    content: [`-1σ (${(mean - stdDev).toFixed(2)})`],
                    position: 'start',
                  },
                },
                // 2nd Standard Deviation lines
                twoStdDevPlus: {
                  type: 'line',
                  xMin: mean + 2 * stdDev,
                  xMax: mean + 2 * stdDev,
                  borderColor: 'rgba(255, 69, 0, 0.5)',
                  borderDash: [5, 5],
                  borderWidth: 2,
                  label: {
                    display: true,
                    content: [`+2σ (${(mean + 2 * stdDev).toFixed(2)})`],
                    position: 'end',
                  },
                },
                twoStdDevMinus: {
                  type: 'line',
                  xMin: mean - 2 * stdDev,
                  xMax: mean - 2 * stdDev,
                  borderColor: 'rgba(255, 69, 0, 0.5)',
                  borderDash: [5, 5],
                  borderWidth: 2,
                  label: {
                    display: true,
                    content: [`-2σ (${(mean - 2 * stdDev).toFixed(2)})`],
                    position: 'start',
                  },
                },
              },
            },
          },
          scales: {
            x: {
              type: 'linear',
              title: { display: true, text: 'Temperature Drop (°C)' },
            },
            y: { title: { display: true, text: 'Probability Density' } },
          },
        },
      });
    }

    // 🍩 Doughnut Chart (no change)
    const doughnutCtx = document.getElementById(
      'doughnutChart'
    ) as HTMLCanvasElement | null;
    if (doughnutCtx) {
      const centerTextPlugin: Plugin<'doughnut'> = {
        id: 'centerText',
        beforeDraw: (chart) => {
          const { ctx, chartArea } = chart;
          const cx = (chartArea.left + chartArea.right) / 2;
          const cy = (chartArea.top + chartArea.bottom) / 2;
          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#2a2f3b';
          ctx.font = '700 22px Arial';
          ctx.fillText(`${totalCount}`, cx, cy - 6);
          ctx.fillStyle = '#6b7280';
          ctx.font = '500 13px Arial';
          ctx.fillText('Records', cx, cy + 16);
          ctx.restore();
        },
      };

      this.doughnutChart = new Chart(doughnutCtx, {
        type: 'doughnut',
        data: {
          labels: [
            `Within 1σ (${oneStdDevPct}%)`,
            `Between 1σ–2σ (${(
              parseFloat(twoStdDevPct) - parseFloat(oneStdDevPct)
            ).toFixed(2)}%)`,
            `Outside 2σ (${outsideStdDevPct}%)`,
          ],
          datasets: [
            {
              data: [
                oneStdDevCount,
                Math.max(twoStdDevCount - oneStdDevCount, 0),
                Math.max(outsideStdDevCount, 0),
              ],
              backgroundColor: ['#6b7280', '#001f40', '#e53935'],
              borderColor: '#fff',
              borderWidth: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '52%',
          plugins: {
            legend: {
              position: 'bottom',
              labels: { boxWidth: 12, padding: 20 },
            },
            tooltip: {
              callbacks: {
                label: function (tooltipItem) {
                  return `${tooltipItem.label}: ${tooltipItem.formattedValue}`;
                },
              },
            },
          },
        },
        plugins: [centerTextPlugin],
      });
    }
  }

  private calculateMean(data: number[]): number {
    return data.length ? data.reduce((a, b) => a + b, 0) / data.length : 0;
  }

  private calculateStandardDeviation(data: number[], mean: number): number {
    if (data.length < 2) return 0;
    const variance =
      data.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / data.length;
    return Math.sqrt(variance);
  }

  private calculateStdDevDistribution(
    data: number[],
    mean: number,
    stdDev: number
  ) {
    if (!data.length || stdDev === 0)
      return { oneStdDevCount: 0, twoStdDevCount: 0, totalCount: 0 };
    const oneMin = mean - stdDev,
      oneMax = mean + stdDev;
    const twoMin = mean - 2 * stdDev,
      twoMax = mean + 2 * stdDev;
    let oneCount = 0,
      twoCount = 0;
    data.forEach((v) => {
      if (v >= oneMin && v <= oneMax) oneCount++;
      if (v >= twoMin && v <= twoMax) twoCount++;
    });
    return {
      oneStdDevCount: oneCount,
      twoStdDevCount: twoCount,
      totalCount: data.length,
    };
  }

  private normalDistributionPDF(
    x: number,
    mean: number,
    stdDev: number
  ): number {
    if (stdDev === 0) return 0;
    const exp = -Math.pow(x - mean, 2) / (2 * Math.pow(stdDev, 2));
    return Math.exp(exp) / (stdDev * Math.sqrt(2 * Math.PI));
  }

  private generateCurvePoints(mean: number, stdDev: number) {
    const points: { x: number; y: number }[] = [];
    if (stdDev <= 0) return points;
    for (let x = mean - 4 * stdDev; x <= mean + 4 * stdDev; x += stdDev / 25) {
      points.push({ x, y: this.normalDistributionPDF(x, mean, stdDev) });
    }
    return points;
  }

  resetSummaryStatsToZero() {
    this.summaryStats = [
      { label: 'Total Records', value: 0 },
      { label: 'Average Temperature Drop', value: '0 °C' },
      { label: 'Median Temperature Drop', value: '0 °C' },
      { label: 'Minimum Temperature Drop', value: '0 °C' },
      { label: 'Maximum Temperature Drop', value: '0 °C' },
      { label: 'Total Cargo Grades', value: 0 },
      { label: 'Total Ships', value: 0 },
      { label: 'Total Voyages', value: 0 },
      { label: 'Minimum Gradient', value: '0 °C' },
      { label: 'Maximum Gradient', value: '0 °C' },
    ];
  }

  getSelectedPlaceholder(selected: string[], defaultText: string): string {
    if (!selected || selected.length === 0) return defaultText;
    if (selected.length > 2) return selected.slice(0, 2).join(', ') + ' ...';
    return selected.join(', ');
  }

  clearForm() {
    this.myForm.reset();
    this.selectedSegments = [];
    this.selectedHeatFlags = [];
    this.selectedCargoTypes = [];
    this.selectedTanks = [];
    this.isFilterSubmitted = false;
    this.noDataFound = false;
    this.showShipTable = false;
    this.temperatureDrops = [];
    this.resetSummaryStatsToZero();
    this.destroyCharts();
  }

  /** Applies a Light Mode host class so scoped light-theme styles can activate. */
  @HostBinding('class.ch-light') get chLight(): boolean {
    return !this.isDarkMode();
  }

  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }
  async downloadAllCharts() {
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text('Charts Report', pageWidth / 2, 15, { align: 'center' });

    // First chart
    await this.addChartToPdf(
      pdf,
      'standardDeviationChart',
      'Standard Deviation Curve',
      40
    );

    // New page for second chart
    pdf.addPage();
    await this.addChartToPdf(pdf, 'doughnutChart', 'Std Dev Distribution', 40);

    // Footer
    pdf.setFontSize(10);
    pdf.setTextColor(150);
    pdf.text(
      'Confidential - Cargo Heating System',
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );

    pdf.save('All-Charts.pdf');
  }


  getTotalColSpan(): number {
    const subCols = this.segmentGroups.reduce((sum, g) => sum + g.columns.length, 0);
    return 1 + subCols;


  }

  getVoyageDrawerTableData(payload?: any) {
    this.voyageTableRows = [];
    this.filteredVoyageRows = [];
    this.voyageTableColumns = [];
    this.recordedTime = '';

    this.commonService.getVoyageData().subscribe({

      next: (res: any) => {
        this.voyageTableRows = [];
        this.filteredVoyageRows = [];
        this.voyageTableColumns = [];
        this.recordedTime = '';

        if (!res || typeof res !== 'object') return;

        this.recordedTime = res?.['Recorded Time'] || '';

        const gradientRanges: string[] = res?.['Gradient Range'] || [];
        if (!gradientRanges.length) return;

        const segments: string[] = Object.keys(res).filter(
          (k) => k !== 'Gradient Range' && k !== 'Recorded Time'
        );

        if (!segments.length) return;

        const firstSegObj = res[segments[0]];
        const tankTypes: string[] =
          firstSegObj && typeof firstSegObj === 'object' ? Object.keys(firstSegObj) : [];

        if (!tankTypes.length) return;


        this.segmentGroups = segments.map((seg) => ({
          name: seg,
          columns: tankTypes.map((tank) => `${seg} - ${tank}`),
          tanks: [...tankTypes]
        }));


        this.voyageTableColumns = ['Gradient Range'];
        segments.forEach((seg) => {
          tankTypes.forEach((tank) => this.voyageTableColumns.push(`${seg} - ${tank}`));
        });


        this.voyageTableRows = gradientRanges.map((range: string, index: number) => {
          const row: any = { gradientRange: range };

          segments.forEach((seg) => {
            tankTypes.forEach((tank) => {
              const colKey = `${seg} - ${tank}`;
              const rawVal = res?.[seg]?.[tank]?.[index];

              if (rawVal === null || rawVal === undefined || rawVal === '') {
                row[colKey] = '—';
              } else {
                const num = Number(rawVal);
                row[colKey] = isNaN(num) ? rawVal : num.toFixed(3);
              }
            });
          });

          return row;
        });

        this.filteredVoyageRows = [...this.voyageTableRows];
      },


      error: () => {
        this.voyageTableRows = [];
        this.filteredVoyageRows = [];
        this.voyageTableColumns = [];
        this.recordedTime = '';
      },
    });
  }


  openVoyageDrawer() {
    this.getVoyageDrawerTableData(this.buildPayload()); // same filters payload
    this.voyageDrawer.open();
  }

  applyFilter(event: any) {
    const value = (event.target.value || '').toLowerCase().trim();

    if (!value) {
      this.filteredVoyageRows = [...this.voyageTableRows];
      return;
    }

    this.filteredVoyageRows = this.voyageTableRows.filter(row => {
      return Object.values(row).some((cell: any) =>
        String(cell).toLowerCase().includes(value)
      );
    });
  }
  refrshdata() {
    this.commonService.calibrateNow().subscribe({
      next: (res: any) => {
        this.toastr.info(res?.message || 'Process started');
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message || 'Something went wrong');
      }
    });
  }



  private async addChartToPdf(
    pdf: jsPDF,
    canvasId: string,
    title: string,
    top: number
  ) {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;

    if (!canvas) {
      console.error('Canvas not found:', canvasId);
      return;
    }

    const imgData = canvas.toDataURL('image/png', 1.0);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height / canvas.width) * imgWidth;

    // Chart title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(50);
    pdf.text(title, pageWidth / 2, top - 10, { align: 'center' });

    // Chart image
    pdf.addImage(imgData, 'PNG', 20, top, imgWidth, imgHeight);
  }

  //  onStatClick(label: string) {
  //   if (label === 'Total Voyages') {
  //     const form = this.myForm.value;

  //     const payload: any = {
  //       segments: this.selectedSegments
  //         .filter(s => s.toLowerCase() !== 'all') // "all" remove
  //         .map(s => s.toLowerCase()),

  //       heat_flags: this.selectedHeatFlags.map(h => h === 'Yes' ? 'Y' : 'N'),
  //       grad_range_min: +form.groupMin,
  //       grad_range_max: +form.groupMax,
  //       cargo_types: this.selectedCargoTypes,
  //       tanks: this.selectedTanks
  //         .filter(t => t !== 'All') // "All" remove
  //         .map(t => t.toLowerCase())
  //     };

  //     this.router.navigate(['/totalShip'], { state: payload });
  //   }
  // }
}

// Model
interface Ship {
  name: string;
  imo: string;
  segment: string;
}
